/**
 * The club's terms of membership — tier copy for the homepage, the membership
 * page and the join flow.
 *
 * These are NOT declared here. `Fireside::Club::Tiers` is the single source of
 * truth for tier names, notes and prices; the engine layout renders it into the
 * shell as `<script type="application/json" id="fireside-club-tiers">` and this
 * module parses it. That is the whole point — the price a member is quoted and
 * the price Stripe charges come from one declaration, so they cannot disagree.
 *
 * Injected rather than fetched from `GET /api/v1/club/tiers` because that
 * endpoint is behind the membership feature flag, and the public landing page
 * would otherwise need a loading state for static product copy.
 *
 * Still presentation only: no Stripe price ids here, and nothing here grants
 * anything. Whether dues can actually be settled is MEMBERSHIP_BILLING_ENABLED.
 */

export interface TierFeature {
  label: string
  /** false renders the hollow ring — promised by a higher tier, not this one. */
  included: boolean
}

export interface MembershipTier {
  id: 'weekender' | 'full' | 'founding'
  name: string
  /** Formatted for display, e.g. "$19". */
  price: string
  /** The billing period, rendered after the price as "/mo" or "/yr". */
  interval: 'mo' | 'yr'
  blurb: string
  features: TierFeature[]
  /** Finite tiers can run out. True once the founding seats are all struck. */
  soldOut: boolean
}

/** One engraved seat on the founding plate (`Club::FoundingRoll#entries`). */
export interface FoundingSeat {
  /** The raw number the club struck — `1`, not "No. 001". */
  member_number: number
  display_name: string
}

/**
 * The founding plate: how many of the seats are struck, how many there are, and
 * who holds them BY NAME.
 *
 * The names are a deliberate decision with the privacy consequence stated and
 * accepted (decision record §7, 2026-08-22 —
 * `../../../../../docs/fireside/club-web-decisions.md`) — see
 * `Fireside::Club::FoundingRoll`, where the reasoning lives. This module only
 * parses it.
 *
 * One asymmetry, and it is the club's rule rather than a bug: the COUNT is
 * seats struck (a number is issued once and never reissued, so a lapsed
 * founder's seat stays spent) while the ROLL is members in good standing. So
 * `roll.length` can be smaller than `taken`, and neither is wrong.
 */
export interface FoundingPlate {
  taken: number
  seats: number
  roll: FoundingSeat[]
}

/** What the server puts in the shell. `interval` is Stripe's word, not ours. */
interface InjectedTier {
  id: MembershipTier['id']
  name: string
  note: string
  price: string
  interval: 'month' | 'year'
  blurb: string
  features: TierFeature[]
  sold_out: boolean
  /** Only on the founding tier — it is the only one with a plate. */
  founding_seats_taken?: number
  founding_seats?: number
  founding_roll?: FoundingSeat[]
}

const INTERVAL_ABBREVIATION: Record<InjectedTier['interval'], MembershipTier['interval']> = {
  month: 'mo',
  year: 'yr',
}

const EMPTY_PLATE: FoundingPlate = { taken: 0, seats: 0, roll: [] }

let cached: MembershipTier[] | null = null
let plate: FoundingPlate = EMPTY_PLATE

/**
 * The tiers, parsed once from the shell.
 *
 * A function rather than a module-load constant so the DOM is read on first
 * use: a constant would be evaluated at import time, which is before a test can
 * seed the payload.
 *
 * Returns [] when the tag is absent — a component rendered outside the engine
 * layout shows no tiers, which is honest. It deliberately does NOT fall back to
 * hard-coded prices; that fallback is the drift this module exists to remove.
 */
export function membershipTiers(): MembershipTier[] {
  if (cached) return cached

  const raw = document.getElementById('fireside-club-tiers')?.textContent
  if (!raw) return []

  try {
    const parsed = JSON.parse(raw) as InjectedTier[]
    cached = parsed.map((tier) => ({
      id: tier.id,
      name: tier.name,
      price: tier.price,
      interval: INTERVAL_ABBREVIATION[tier.interval],
      blurb: tier.blurb,
      features: tier.features,
      soldOut: Boolean(tier.sold_out),
    }))
    notes = Object.fromEntries(parsed.map((tier) => [tier.id, tier.note])) as Record<
      MembershipTier['id'],
      string
    >
    const founding = parsed.find((tier) => tier.id === 'founding')
    plate = {
      taken: founding?.founding_seats_taken ?? 0,
      seats: founding?.founding_seats ?? 0,
      roll: founding?.founding_roll ?? [],
    }
    return cached
  } catch {
    // Malformed payload is a server bug; render nothing rather than a broken page.
    return []
  }
}

/** Test seam: forget the parsed payload so a fresh DOM is read. */
export function resetMembershipTiersCache(): void {
  cached = null
  plate = EMPTY_PLATE
  notes = {} as Record<MembershipTier['id'], string>
}

/**
 * The founding plate, parsed from the same injection as the tiers — it hangs
 * off the founding TIER, which is the one tier those facts are about.
 *
 * `seats: 0` means the shell said NOTHING about the plate (a component rendered
 * outside the engine layout). A page must read that as "no answer" and print no
 * counter at all, rather than falling back to a hardcoded hundred — that
 * fallback is exactly the drift this module exists to remove.
 */
export function foundingPlate(): FoundingPlate {
  if (cached === null) membershipTiers()
  return plate
}

/**
 * "No. 007" from the raw number the club struck.
 *
 * ⚠️ The register and the founding roll carry member numbers as INTEGERS
 * (`register_entry_json`, `Club::FoundingRoll#entries`), while the membership
 * payload behind `useClubMembership` carries the FORMATTED string
 * (`ClubMembership#formatted_number`). Two shapes, one club: format here, and
 * never assume the wire already did.
 */
export function formatMemberNumber(number: number | null): string | null {
  if (number === null) return null
  return `No. ${String(number).padStart(3, '0')}`
}

export function findTier(id: string | undefined): MembershipTier | undefined {
  return membershipTiers().find((tier) => tier.id === id)
}

/**
 * Mirrors `Fireside::Club::Tiers::DEFAULT_ID`. Named rather than positional:
 * the checkout used to fall back to `membershipTiers()[1]`, which was Full only
 * because the Ruby array happens to be ordered weekender/full/founding —
 * reordering it there would silently have changed the price charged here.
 *
 * It is a last resort, not a preselection. The checkout asks which standing is
 * meant rather than assuming this one.
 */
export const DEFAULT_TIER_ID = 'full'

/**
 * The privileges matrix behind /membership — the same promises as the tier
 * cards, laid out row-per-privilege so the standings can be compared.
 *
 * A value is either a boolean (rendered as the filled/hollow brass ring) or a
 * string, for privileges that differ by degree rather than presence.
 */
export type PrivilegeValue = boolean | string

export interface Privilege {
  label: string
  weekender: PrivilegeValue
  full: PrivilegeValue
  founding: PrivilegeValue
}

export const PRIVILEGES: Privilege[] = [
  { label: 'Full run of the grounds', weekender: true, full: true, founding: true },
  { label: 'Personal scorecard & handicap', weekender: true, full: true, founding: true },
  { label: 'The Register — standings & roster', weekender: false, full: true, founding: true },
  { label: 'The Lounge — between rounds', weekender: false, full: true, founding: true },
  { label: 'Private groups & club nights', weekender: false, full: true, founding: true },
  { label: 'Guest passes', weekender: false, full: 'Four a season', founding: 'Four a season' },
  { label: 'Founding seal on your card', weekender: false, full: false, founding: true },
  { label: 'Name inscribed in the charter', weekender: false, full: false, founding: true },
  { label: 'Rate held for life', weekender: false, full: false, founding: true },
]

let notes = {} as Record<MembershipTier['id'], string>

/**
 * Sub-heading under each tier name in the matrix — also from Club::Tiers, so
 * it cannot drift from the tier it describes. Populated by membershipTiers().
 */
export function tierNote(id: MembershipTier['id']): string {
  if (Object.keys(notes).length === 0) membershipTiers()
  return notes[id] ?? ''
}

/**
 * Whether dues can actually be settled — `Fireside::Club::FeatureFlag`, stamped
 * into the shell as a meta tag by the engine layout. Default OFF.
 *
 * Read once at module load: the flag is decided server-side per request, so it
 * cannot change under a mounted SPA.
 */
export const MEMBERSHIP_BILLING_ENABLED =
  document.querySelector<HTMLMetaElement>('meta[name="fireside-membership-enabled"]')?.content ===
  'true'
